import { SlotImg, ReplaceSlot, useSlot, naturalRatio } from "@/components/site/AssetImage";

const SKIN = ["#F1C9A8", "#C68C66", "#8A5A3C", "#E6B38E"];
const HAIR = ["#3B2A20", "#6B4428", "#1F1A17", "#A0673A"];

const Box = ({ x, y, w, h, color, ribbon = "#FFF3C4", className }) => (
  <g className={className}>
    <rect x={x} y={y} width={w} height={h} rx="4" fill={color} />
    <rect x={x + w / 2 - 3} y={y} width="6" height={h} fill={ribbon} />
    <rect x={x} y={y + h / 2 - 3} width={w} height="6" fill={ribbon} opacity="0.9" />
    <ellipse cx={x + w / 2 - 6} cy={y - 3} rx="7" ry="4" fill={ribbon} />
    <ellipse cx={x + w / 2 + 6} cy={y - 3} rx="7" ry="4" fill={ribbon} />
  </g>
);

function Person({ x, y, s = 1, shirt, skin = 0, hair = 0, pants = "#3B4A44", smile = false, carry, scarf }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <rect x="-9" y="-30" width="7" height="30" rx="3.5" fill={pants} />
      <rect x="2" y="-30" width="7" height="30" rx="3.5" fill={pants} />
      <rect x="-13" y="-62" width="26" height="36" rx="12" fill={shirt} />
      {scarf && <rect x="-11" y="-62" width="22" height="6" rx="3" fill={scarf} />}
      <circle cx="0" cy="-74" r="11" fill={SKIN[skin]} />
      <path d="M-11 -75 a11 11 0 0 1 22 0 q-11 -6 -22 0z" fill={HAIR[hair]} />
      {smile && <path d="M-4 -70 q4 3.5 8 0" stroke="#5A3A2A" strokeWidth="1.6" fill="none" strokeLinecap="round" />}
      {carry && <Box x={-15} y={-58} w={30} h={22} color={carry} />}
    </g>
  );
}

const Frame = ({ bg, children }) => (
  <svg viewBox="0 0 320 220" className="h-full w-full" aria-hidden="true" focusable="false">
    <rect width="320" height="220" fill={bg} />
    {children}
    <rect y="190" width="320" height="30" fill="#E2EEE8" />
  </svg>
);

const HowStores = () => (
  <Frame bg="#FFF3F6">
    <circle cx="60" cy="50" r="26" fill="#FFE7A8" opacity="0.7" />
    <rect x="160" y="70" width="140" height="120" rx="6" fill="#FFFCF8" stroke="#EADFD8" strokeWidth="2" />
    {[0, 1, 2, 3, 4, 5, 6].map((i) => <rect key={i} x={160 + i * 20} y="58" width="20" height="22" rx="4" fill={i % 2 ? "#FFFCF8" : "#F3AFC2"} />)}
    <rect x="175" y="98" width="58" height="44" rx="4" fill="#E2EEE8" />
    <rect x="248" y="108" width="36" height="82" rx="4" fill="#BED8CC" />
    {[[52, 32], [106, 22], [78, 12]].map(([x, r], i) => <ellipse key={i} cx={x} cy="190" rx={r} ry="4" fill="#24453D" opacity="0.1" />)}
    <Box x={28} y={128} w={48} h={42} color="#C73450" className="of-float" />
    <Box x={90} y={142} w={34} h={30} color="#F3AFC2" ribbon="#C73450" className="of-float of-float-2" />
    <Box x={60} y={86} w={34} h={28} color="#E46A82" className="of-float of-float-3" />
  </Frame>
);

const HowLoading = () => (
  <Frame bg="#F3F8F5">
    <path d="M150 175 L150 128 Q152 112 170 110 L230 108 Q246 92 268 94 L290 98 Q306 104 306 124 L306 175 Z" fill="#BED8CC" />
    <path d="M238 110 L254 98 L284 100 L292 112 Z" fill="#24453D" opacity="0.8" />
    <path d="M150 128 L132 96" stroke="#24453D" strokeWidth="5" strokeLinecap="round" />
    <Box x={156} y={124} w={26} h={22} color="#C73450" />
    <Box x={186} y={128} w={22} h={18} color="#F3AFC2" ribbon="#C73450" />
    {[180, 276].map((x) => <g key={x}><circle cx={x} cy="178" r="14" fill="#24453D" /><circle cx={x} cy="178" r="5" fill="#FFFCF8" /></g>)}
    <Person x={62} y={190} shirt="#9CC3B1" skin={2} hair={2} carry="#F3AFC2" />
    <Person x={110} y={190} shirt="#BED8CC" skin={0} hair={1} carry="#C73450" scarf="#24453D" />
  </Frame>
);

const HowSorting = () => (
  <Frame bg="#FFF6F8">
    <path d="M10 30 Q160 60 310 30" stroke="#C9B8B0" strokeWidth="1.5" fill="none" />
    {[30, 70, 110, 150, 190, 230, 270].map((x, i) => (
      <path key={x} d={`M${x} ${36 + (i === 3 ? 8 : Math.abs(3 - i) * 0)} l10 0 l-5 12 z`} fill={i % 2 ? "#BED8CC" : "#F3AFC2"} />
    ))}
    <Person x={95} y={182} s={0.82} shirt="#F3AFC2" skin={0} hair={1} smile />
    <Person x={160} y={182} s={0.82} shirt="#BED8CC" skin={1} hair={0} smile />
    <Person x={225} y={182} s={0.82} shirt="#F3AFC2" skin={2} hair={2} smile />
    <rect x="40" y="150" width="240" height="12" rx="6" fill="#C99A6B" />
    <rect x="58" y="160" width="8" height="30" fill="#A87C52" />
    <rect x="254" y="160" width="8" height="30" fill="#A87C52" />
    <rect x="70" y="132" width="16" height="16" rx="3" fill="#C73450" />
    <rect x="86" y="132" width="16" height="16" rx="3" fill="#BED8CC" />
    <rect x="78" y="116" width="16" height="16" rx="3" fill="#F5AE3D" />
    <circle cx="135" cy="139" r="11" fill="#F3AFC2" />
    <path d="M124 139 h22" stroke="#FFFCF8" strokeWidth="3" />
    <rect x="182" y="134" width="34" height="12" rx="5" fill="#9CC3B1" />
    <circle cx="190" cy="148" r="4" fill="#24453D" />
    <circle cx="208" cy="148" r="4" fill="#24453D" />
    <rect x="232" y="132" width="26" height="16" rx="2" fill="#E46A82" />
  </Frame>
);

const Building = ({ x = 180 }) => (
  <g>
    <rect x={x} y="48" width="124" height="142" rx="6" fill="#FFFCF8" stroke="#EADFD8" strokeWidth="2" />
    {[0, 1, 2].map((r) => [0, 1, 2].map((c) => <rect key={`${r}${c}`} x={x + 14 + c * 36} y={62 + r * 28} width="24" height="18" rx="3" fill="#FFE7A8" />))}
    <circle cx={x + 62} cy="160" r="34" fill="#FFE7A8" opacity="0.55" />
    <path d={`M${x + 44} 190 V160 a18 18 0 0 1 36 0 V190 Z`} fill="#BED8CC" />
    <rect x={x + 34} y="140" width="56" height="7" rx="3.5" fill="#24453D" />
    <path d={`M${x + 62} 136 c-6 -8 -16 -2 -10 5 l10 8 l10 -8 c6 -7 -4 -13 -10 -5z`} fill="#F3AFC2" transform={`translate(0 -14)`} />
  </g>
);

const HowHospital = () => (
  <Frame bg="#FFF4EC">
    <Building />
    <Person x={50} y={190} shirt="#24453D" skin={3} hair={0} carry="#C73450" />
    <Person x={98} y={190} shirt="#BED8CC" skin={1} hair={3} carry="#F3AFC2" scarf="#24453D" />
    <Person x={146} y={190} shirt="#2F5A4F" skin={2} hair={2} carry="#E46A82" />
  </Frame>
);

const CollectStore = () => (
  <Frame bg="#F3F8F5">
    <rect x="20" y="40" width="180" height="10" rx="5" fill="#C99A6B" />
    <rect x="20" y="84" width="180" height="10" rx="5" fill="#C99A6B" />
    {[30, 60, 90, 120, 150, 172].map((x, i) => <rect key={x} x={x} y={22} width="20" height="18" rx="3" fill={["#F3AFC2", "#BED8CC", "#FFE7A8"][i % 3]} />)}
    {[34, 70, 104, 140, 168].map((x, i) => <rect key={x} x={x} y={64} width="22" height="20" rx="3" fill={["#BED8CC", "#FFE7A8", "#F3AFC2"][i % 3]} />)}
    <rect x="40" y="140" width="80" height="50" rx="5" fill="#C73450" />
    <path d="M80 152 c-5 -7 -14 -2 -9 4 l9 7 l9 -7 c5 -6 -4 -11 -9 -4z" fill="#FFFCF8" />
    <circle cx="58" cy="136" r="9" fill="#F3AFC2" />
    <rect x="70" y="124" width="16" height="16" rx="3" fill="#F5AE3D" />
    <rect x="92" y="128" width="20" height="12" rx="4" fill="#9CC3B1" />
    <rect x="150" y="120" width="12" height="12" rx="3" fill="#F5AE3D" className="of-float" />
    <Person x={190} y={190} shirt="#F3AFC2" skin={1} hair={0} />
    <Person x={250} y={190} shirt="#BED8CC" skin={3} hair={3} scarf="#C73450" />
  </Frame>
);

const SortChurch = () => (
  <Frame bg="#FFF6F8">
    {[40, 130, 220].map((x) => <path key={x} d={`M${x} 110 V60 a30 30 0 0 1 60 0 V110 Z`} fill="#FCE4EB" stroke="#EADFD8" strokeWidth="2" />)}
    <Person x={110} y={182} shirt="#BED8CC" skin={0} hair={2} />
    <Person x={214} y={182} shirt="#24453D" skin={2} hair={0} />
    <rect x="30" y="150" width="260" height="12" rx="6" fill="#C99A6B" />
    {[[50, "#BED8CC"], [130, "#F3AFC2"], [210, "#FFE7A8"]].map(([x, c]) => (
      <g key={x}>
        <rect x={x} y="126" width="60" height="26" rx="5" fill={c} />
        <rect x={x + 8} y="116" width="12" height="12" rx="2" fill="#C73450" />
        <circle cx={x + 34} cy="120" r="7" fill="#9CC3B1" />
        <rect x={x + 44} y="114" width="10" height="14" rx="2" fill="#F5AE3D" />
      </g>
    ))}
    <rect x="44" y="160" width="8" height="30" fill="#A87C52" />
    <rect x="268" y="160" width="8" height="30" fill="#A87C52" />
  </Frame>
);

const DeliverHospital = () => (
  <Frame bg="#FFF4EC">
    <Building x={170} />
    <path d="M86 140 h40 l-4 42 h-32 z" fill="#F3AFC2" />
    <path d="M96 140 q10 -16 20 0" stroke="#C73450" strokeWidth="3" fill="none" />
    <rect x="100" y="132" width="10" height="10" rx="2" fill="#F5AE3D" />
    <Person x={66} y={190} shirt="#24453D" skin={1} hair={1} scarf="#BED8CC" />
    <Person x={150} y={190} shirt="#BED8CC" skin={3} hair={0} />
    <path d="M144 128 l6 14 l6 -14" stroke="#24453D" strokeWidth="2" fill="none" />
  </Frame>
);

const SCENES = {
  HOW_1: HowStores, HOW_2: HowLoading, HOW_3: HowSorting, HOW_4: HowHospital,
  CSD_1: CollectStore, CSD_2: SortChurch, CSD_3: DeliverHospital,
};

export function IllustrationSlot({ asset, className = "", testId, children }) {
  const { a, up, alt } = useSlot(asset);
  if (up) {
    return (
      <div className={`relative ${className}`} style={{ aspectRatio: naturalRatio(up) }}>
        <SlotImg up={up} alt={alt} fade sizes="(min-width: 1024px) 400px, 100vw" className="h-full w-full" testId={testId} />
        <ReplaceSlot asset={asset} />
      </div>
    );
  }
  return (
    <div className={`relative ${className}`}>
      <div role="img" aria-label={`${a.label}: ${alt}`} data-placeholder={a.label} className="h-full w-full" data-testid={testId}>{children}</div>
      <ReplaceSlot asset={asset} />
    </div>
  );
}

export function Scene({ asset, className = "", testId }) {
  const Svg = SCENES[asset];
  return <IllustrationSlot asset={asset} className={`overflow-hidden ${className}`} testId={testId}><Svg /></IllustrationSlot>;
}
